/* Rhody Reviews api/v1 -- locations domain (multi-location foundation).

   Client for /api/v1/locations CRUD. The backend routes are gated behind
   LOCATIONS_API_ENABLED (default OFF) and return 404 until per-location
   provisioning is built, so callers should treat a 404 as "multi-location
   management is not turned on yet", not an error to surface loudly.

   IMPORTANT (honesty): creating a location today stores a record with
   status='pending_provision'. It does NOT yet get its own dedicated phone
   number, its own connected Google Business Profile, or its own review-request
   routing. Those are the scope items Steve must sign off (see the 2026-08-18
   build plan). Do not present a created location as fully live. */
import { request } from './_http';

export type LocationStatus = 'active' | 'paused' | 'pending_provision';

export interface Location {
  id: string;
  client_id: string;
  label: string;
  google_location_id?: string | null;
  yelp_business_id?: string | null;
  facebook_page_id?: string | null;
  twilio_phone_number?: string | null;
  voice_overrides?: unknown;
  status: LocationStatus;
  created_at?: string | null;
  updated_at?: string | null;
  [k: string]: unknown;
}

export interface LocationsResponse {
  client_id: string;
  count: number;
  locations: Location[];
}

/* Owner-editable fields only. The system/provisioning columns
   (twilio_phone_number, stripe_subscription_item_id, status) are NOT settable
   here -- the server ignores them. */
export interface LocationInput {
  label: string;
  google_location_id?: string;
  yelp_business_id?: string;
  facebook_page_id?: string;
  voice_overrides?: unknown;
}

export const locationsApi = {
  listLocations(signal?: AbortSignal): Promise<LocationsResponse> {
    return request<LocationsResponse>('/api/v1/locations', { signal });
  },
  createLocation(
    input: LocationInput,
  ): Promise<{ client_id: string; id: string; location: Location }> {
    return request<{ client_id: string; id: string; location: Location }>(
      '/api/v1/locations',
      { method: 'POST', body: input },
    );
  },
  updateLocation(
    id: string,
    fields: Partial<LocationInput>,
  ): Promise<{ client_id: string; location: Location }> {
    return request<{ client_id: string; location: Location }>(
      `/api/v1/locations/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: fields },
    );
  },
  deleteLocation(id: string): Promise<{ client_id: string; deleted: string }> {
    return request<{ client_id: string; deleted: string }>(
      `/api/v1/locations/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
  },
};
